import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from '@/hooks/useTranslation';
import { useGeoRegion } from '@/hooks/useGeoRegion';

type Mode = 'general' | 'code' | 'analysis' | 'content' | 'rag';
type LoginPhase = 'idle' | 'name' | 'email' | 'done';

interface Message {
  role: 'ai' | 'user';
  text: string;
  isGate?: boolean;
}

const modeIcons: Record<Mode, string> = {
  general: '💬', code: '💻', analysis: '📊', content: '✍️', rag: '🔍',
};

const PlaygroundSection = () => {
  const { t } = useTranslation();
  const pg = t.playground as any;
  const alexT = t.alex as any;
  const { isEurope } = useGeoRegion();
  const SESSION_LIMIT = isEurope ? 10 : 2;

  const [mode, setMode] = useState<Mode>('general');
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionCount, setSessionCount] = useState(0);
  const [gated, setGated] = useState(false);

  // Login gate — starts idle
  const [loginPhase, setLoginPhase] = useState<LoginPhase>('idle');
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [sessionId] = useState(() => crypto.randomUUID());
  const [emailAttempts, setEmailAttempts] = useState(0);
  const [pendingQuestion, setPendingQuestion] = useState('');

  // Init with greeting
  useEffect(() => {
    setMessages([{ role: 'ai', text: pg.greetings[mode] }]);
  }, []);

  // VIP IP auto-recognition on mount
  useEffect(() => {
    const checkVip = async () => {
      try {
        const { data } = await supabase.functions.invoke('chat-playground', {
          body: { checkVipIp: true },
        });
        if (data?.vip) {
          setVisitorName(data.name || '');
          setVisitorEmail(data.email || '');
          setLoginPhase('done');
        }
      } catch (_e) { /* ignore */ }
    };
    checkVip();
  }, []);

  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Auto-send pending question after login completes
  useEffect(() => {
    if (loginPhase === 'done' && pendingQuestion) {
      const q = pendingQuestion;
      setPendingQuestion('');
      sendMessageToAI(q);
    }
  }, [loginPhase]);

  const switchMode = (newMode: Mode) => {
    setMode(newMode);
    setMessages([{ role: 'ai', text: pg.greetings[newMode] }]);
    setInput('');
    setIsTyping(false);
  };

  const showGateMessage = () => {
    const gateMsg = !isEurope ? (pg.gateRestricted?.message || pg.gate?.message) : pg.gate?.message;
    const gateText = (gateMsg || `You've reached the session limit of ${SESSION_LIMIT} messages.`).replace('{count}', String(SESSION_LIMIT));
    setMessages(prev => [...prev, { role: 'ai', text: gateText, isGate: true }]);
    setGated(true);
  };

  const handleLoginInput = async (text: string) => {
    if (!text.trim()) return;

    if (loginPhase === 'name') {
      const name = text.trim();
      setVisitorName(name);
      const userMsg: Message = { role: 'user', text: name };
      const askEmailText = (alexT.askEmail || "Nice to meet you, {name}! Drop me your email so I know you're a real person.").replace('{name}', name);
      setMessages(prev => [...prev, userMsg, { role: 'ai', text: askEmailText }]);
      setInput('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      setLoginPhase('email');
      return;
    }

    if (loginPhase === 'email') {
      const emailInput = text.trim();
      const userMsg: Message = { role: 'user', text: emailInput };
      setMessages(prev => [...prev, userMsg]);
      setInput('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailInput)) {
        setMessages(prev => [...prev, { role: 'ai', text: alexT.emailInvalid || "That doesn't look like a real email address — mind double-checking?" }]);
        return;
      }

      setIsTyping(true);
      try {
        const { data, error } = await supabase.functions.invoke('chat-playground', {
          body: { validateEmailOnly: true, visitorEmail: emailInput, visitorName, sessionId, source: 'playground' },
        });

        if (error) throw error;

        if (data?.emailInvalid) {
          setEmailAttempts(prev => prev + 1);
          const reason = data.reason;
          let responseText: string;
          if (reason === 'disposable') {
            responseText = alexT.emailDisposable || "Nice try! But I need a real email, not a throwaway one.";
          } else if (reason === 'suspicious') {
            responseText = alexT.emailSuspicious || "That email looks a bit... creative. I need a real one you actually check — no test addresses or keyboard mashing, please.";
          } else if (emailAttempts >= 1) {
            responseText = alexT.emailRefused || "I get it, privacy matters. But without an email I can't tell you apart from the bots.";
          } else {
            responseText = alexT.emailInvalid || "That doesn't look like a real email address — mind double-checking?";
          }
          setMessages(prev => [...prev, { role: 'ai', text: responseText }]);
          return;
        }

        setVisitorEmail(emailInput);
        setLoginPhase('done');
        const welcomeText = (alexT.welcomeBack || "Hey {name}! Good to have you. What can I help with?").replace('{name}', visitorName);
        setMessages(prev => [...prev, { role: 'ai', text: welcomeText }]);
      } catch {
        setMessages(prev => [...prev, { role: 'ai', text: pg.errorMessage || "Something went wrong. Try again?" }]);
      } finally {
        setIsTyping(false);
      }
      return;
    }
  };

  /** Send directly to AI (post-login or example click) */
  const sendMessageToAI = async (text: string, isExample = false) => {
    const userMsg: Message = { role: 'user', text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);

    if (!isExample) {
      const newCount = sessionCount + 1;
      setSessionCount(newCount);
      if (newCount > SESSION_LIMIT) {
        showGateMessage();
        return;
      }
    }

    setIsTyping(true);

    try {
      const { data, error } = await supabase.functions.invoke('chat-playground', {
        body: {
          messages: updatedMessages,
          systemPrompt: pg.systemPrompts[mode],
          source: 'playground',
          visitorName: visitorName || undefined,
          visitorEmail: visitorEmail || undefined,
          sessionId,
        },
      });

      if (data?.rateLimited) {
        setMessages(prev => [...prev, { role: 'ai', text: data.reply, isGate: true }]);
        setGated(true);
        return;
      }

      if (error || data?.error) throw new Error(data?.error || error?.message || 'Unknown error');
      setMessages(prev => [...prev, { role: 'ai', text: data.reply }]);
    } catch (err: any) {
      if (err?.message?.includes('429') || err?.status === 429) {
        showGateMessage();
        return;
      }
      console.error('Chat error:', err);
      setMessages(prev => [...prev, { role: 'ai', text: pg.errorMessage }]);
    } finally {
      setIsTyping(false);
    }
  };

  const sendMessage = async (text?: string, isExample = false) => {
    const msg = (text || input).trim();
    if (!msg || isTyping || gated) return;
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    // Examples/chips bypass login gate
    if (isExample) {
      await sendMessageToAI(msg, true);
      return;
    }

    // Free-form input: if idle, trigger gate
    if (loginPhase === 'idle') {
      setPendingQuestion(msg);
      const askNameText = alexT.askNameBeforeAnswer || "Hold on — before I answer that, what's your name?";
      setMessages(prev => [...prev, { role: 'user', text: msg }, { role: 'ai', text: askNameText }]);
      setLoginPhase('name');
      return;
    }

    // If in login flow
    if (loginPhase !== 'done') {
      handleLoginInput(msg);
      return;
    }

    // Logged in
    await sendMessageToAI(msg);
  };

  const handleKeypress = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  const formatText = (text: string) => {
    return text
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" style="color:var(--amber);text-decoration:underline">$1</a>')
      .replace(/\[([^\]]+)\]\((mailto:[^\s)]+)\)/g, '<a href="$2" style="color:var(--amber);text-decoration:underline">$1</a>')
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>');
  };

  const modes: Mode[] = ['general', 'code', 'analysis', 'content', 'rag'];

  return (
    <section id="playground" className="pg-section">
      <div className="pg-header">
        <div className="section-tag" style={{ color: 'var(--amber)', justifyContent: 'center' }}>
          <span>{pg.tag}</span>
        </div>
        <h2 className="section-h" style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto 18px' }}>
          {pg.title} <span className="a">{pg.titleAccent}</span>
        </h2>
        <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
          {pg.subtitle}
        </p>
      </div>

      <div className="playground-shell">
        <div className="pg-topbar">
          <div className="pg-dots"><div className="pg-dot" /><div className="pg-dot" /><div className="pg-dot" /></div>
          <div className="pg-title">{pg.topbarTitle}</div>
          <div className="pg-status"><span className="pg-status-dot" />{pg.statusLabel}</div>
          {!isEurope && <span className="pg-limited-badge">Limited Demo</span>}
        </div>

        <div className="pg-body">
          <div className="pg-sidebar">
            <div className="pg-sidebar-label">{pg.sidebarLabel}</div>
            {modes.map(m => (
              <div key={m} className={`pg-mode${mode === m ? ' active' : ''}`} onClick={() => switchMode(m)}>
                <span>{modeIcons[m]}</span>
                <span>{pg.modes[m]}</span>
                {pg.badges[m] && <span className="pg-badge">{pg.badges[m]}</span>}
              </div>
            ))}
            <div className="pg-examples-title">{pg.examplesTitle}</div>
            {pg.examples.map((ex: string, i: number) => (
              <div key={i} className="pg-example" onClick={() => sendMessage(ex, true)}>{ex}</div>
            ))}
          </div>

          <div className="pg-main">
            <div className="pg-messages" ref={messagesRef}>
              {messages.map((msg, i) => (
                <div key={i} className={`pg-msg ${msg.role === 'user' ? 'user' : 'ai'}${msg.isGate ? ' gate' : ''}`}>
                  <div className="pg-msg-av">{msg.role === 'ai' ? 'ADDIS' : '👤'}</div>
                  <div className="pg-bubble" dangerouslySetInnerHTML={{ __html: formatText(msg.text) }} />
                </div>
              ))}
              {isTyping && (
                <div className="pg-msg ai">
                  <div className="pg-msg-av">ADDIS</div>
                  <div className="pg-bubble"><div className="pg-typing"><span /><span /><span /></div></div>
                </div>
              )}

              {gated && (
                <div className="pg-gate-cta">
                  <a href="/#contact" className="pg-gate-btn primary">{pg.gate?.cta1 || 'Schedule a Consultation'}</a>
                  <a href="mailto:contact@add-individual-solutions.com" className="pg-gate-btn secondary">{pg.gate?.cta2 || 'Contact Us'}</a>
                </div>
              )}
            </div>

            <div className="pg-input-area">
              <textarea
                ref={textareaRef}
                className="pg-textarea"
                placeholder={
                  gated
                    ? (pg.gate?.title || 'Session limit reached')
                    : loginPhase === 'name'
                    ? (alexT.namePlaceholder || 'Your name...')
                    : loginPhase === 'email'
                    ? (alexT.emailPlaceholder || 'Your email...')
                    : pg.placeholder
                }
                value={input} onChange={e => { setInput(e.target.value); autoResize(e.target); }}
                onKeyDown={handleKeypress} rows={1} disabled={gated}
              />
              <button className="pg-send" onClick={() => sendMessage()} disabled={gated}>➤</button>
            </div>
            <div className="pg-disclaimer">
              {pg.disclaimer}
              {!gated && sessionCount > 0 && loginPhase === 'done' && (
                <span style={{ marginLeft: 12, opacity: 0.5, fontSize: '0.8em' }}>
                  {sessionCount}/{SESSION_LIMIT}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlaygroundSection;
